import type { LogFn } from '../../log.js';
import type { CliE2eeRuntime } from '../../lib/e2ee/index.js';
import type WebSocket from 'ws';
import { createPreviewEnvironmentManagerDeps } from './create-deps.js';
import type { PreviewEnvironmentManagerDeps } from './create-deps.js';
import {
  applyPreviewEnvironmentConfig,
  startPreviewEnvironmentById,
} from './ops.js';
import { shutdownAllPreviewEnvironments } from './shutdown-all.js';
import { stopPreviewEnvironment } from './stop.js';

export type PreviewEnvironmentManagerOptions = {
  getWs: () => WebSocket | null;
  log: LogFn;
  getBridgeRoot?: () => string;
  e2ee?: CliE2eeRuntime;
  getPreviewWorktreeParentPath?: (environmentId: string) => string | undefined;
};

export class PreviewEnvironmentManager {
  private readonly deps: PreviewEnvironmentManagerDeps;

  constructor(options: PreviewEnvironmentManagerOptions) {
    this.deps = createPreviewEnvironmentManagerDeps(options);
  }

  private get state() {
    return this.deps.state;
  }

  private get runtime() {
    return this.deps.runtime;
  }

  private get firehoseSink() {
    return this.deps.firehoseSink;
  }

  attachFirehose(send: (msg: Record<string, unknown>) => void): void {
    this.firehoseSink.attach(send);
  }

  detachFirehose(): void {
    this.firehoseSink.detach();
  }

  handleFirehoseLogViewerOpen(environmentId: string, viewerId: string): void {
    this.firehoseSink.openLogViewer(environmentId, viewerId);
  }

  handleFirehoseLogViewerClose(environmentId: string, viewerId: string): void {
    this.firehoseSink.closeLogViewer(environmentId, viewerId);
  }

  applyConfig(servers: unknown): void {
    applyPreviewEnvironmentConfig(this.state, servers);
  }

  stop(environmentId: string): void {
    stopPreviewEnvironment(this.runtime, environmentId);
    this.firehoseSink.refreshLogViewers(environmentId);
  }

  start(environmentId: string): void {
    startPreviewEnvironmentById(this.runtime, this.state, environmentId);
  }

  handleControl(environmentId: string, action: 'start' | 'stop'): void {
    if (action === 'stop') {
      this.stop(environmentId);
      return;
    }
    this.start(environmentId);
  }

  async shutdownAllGraceful(opts?: { graceMs?: number }): Promise<void> {
    await shutdownAllPreviewEnvironments(this.runtime, opts);
  }
}
